import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import data from "../../data/index.json";
import "./MyPortfolio.css";

const placeholder =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='100%'><stop offset='0%' stop-color='%23f8fafc'/><stop offset='100%' stop-color='%23e2e8f0'/></linearGradient></defs><rect width='100%' height='100%' fill='url(%23grad)'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Inter, sans-serif' font-size='18' fill='%2394a3b8'>No preview</text></svg>`
  );

export default function MyPortfolio() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariant = {
    hidden: { 
      opacity: 0, 
      y: 30, 
      scale: 0.98,
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        ease: [0.25, 0.46, 0.45, 0.94],
      } 
    },
  };

  const imageVariant = {
    hidden: { scale: 1.1 },
    visible: { 
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    },
  };

  return (
    <section 
      ref={sectionRef}
      className="portfolio-section" 
      id="MyPortfolio" 
      aria-labelledby="portfolio-heading"
    >
      <div className="portfolio-container">
        {/* Enhanced Header */}
        <motion.div 
          className="portfolio-header"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="header-content">
            <motion.p 
              className="sub-title"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Selected Works
            </motion.p>
            <motion.h2 
              id="portfolio-heading" 
              className="section-heading"
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Portfolio Showcase
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a
              href="https://github.com/romyo1999"
              className="btn btn-github"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit my GitHub profile"
            >
              <svg width="20" height="20" viewBox="0 0 33 33" fill="none" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.3333 0.166748C7.50028 0.166748 0.333252 7.33378 0.333252 16.1667C0.333252 24.9997 7.50028 32.1667 16.3333 32.1667C25.1489 32.1667 32.3333 24.9997 32.3333 16.1667C32.3333 7.33378 25.1489 0.166748 16.3333 0.166748ZM26.9016 7.54202C28.8105 9.8674 29.9559 12.8348 29.9906 16.0452C29.5394 15.9585 25.0274 15.0387 20.4808 15.6114C20.3767 15.3858 20.2899 15.1428 20.1858 14.8999C19.9081 14.2405 19.5958 13.5637 19.2834 12.9216C24.3159 10.8739 26.6066 7.9238 26.9016 7.54202ZM16.3333 2.52684C19.804 2.52684 22.9797 3.82836 25.3919 5.96285C25.1489 6.30992 23.0838 9.06914 18.2248 10.8912C15.9862 6.77846 13.5047 3.41187 13.1229 2.89126C14.1467 2.64831 15.2227 2.52684 16.3333 2.52684ZM10.5199 3.811C10.8843 4.2969 13.3138 7.68085 15.5871 11.7068C9.20093 13.4075 3.56102 13.3728 2.95364 13.3728C3.83867 9.13855 6.70201 5.61577 10.5199 3.811ZM2.65863 16.1841C2.65863 16.0452 2.65863 15.9064 2.65863 15.7676C3.24865 15.7849 9.87772 15.8717 16.6977 13.824C17.0969 14.5875 17.4613 15.3684 17.8084 16.1493C17.6348 16.2014 17.4439 16.2535 17.2704 16.3055C10.2248 18.5788 6.47642 24.7914 6.16405 25.312C3.99485 22.8999 2.65863 19.6895 2.65863 16.1841ZM16.3333 29.8413C13.1749 29.8413 10.2595 28.7654 7.95147 26.9606C8.19442 26.4574 10.971 21.1125 18.676 18.4227C18.7107 18.4053 18.7281 18.4053 18.7628 18.388C20.689 23.3684 21.47 27.5506 21.6782 28.748C20.0296 29.4595 18.2248 29.8413 16.3333 29.8413ZM23.9515 27.4986C23.8127 26.6656 23.0838 22.6743 21.2964 17.7632C25.5828 17.0864 29.3311 18.1971 29.7997 18.3533C29.2097 22.1537 27.0231 25.4335 23.9515 27.4986Z"
                  fill="currentColor"
                />
              </svg>
              Explore GitHub
            </a>
          </motion.div>
        </motion.div>

        {/* Enhanced Grid */}
        <motion.div
          className="portfolio-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {data?.portfolio?.length ? (
            data.portfolio.map((item, idx) => (
              <motion.article
                key={item.id ?? idx}
                className="portfolio-card"
                variants={cardVariant}
                whileHover={{ 
                  y: -12, 
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                whileTap={{ scale: 0.98 }}
              >
                <a
                  className="portfolio-thumb"
                  href={item.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View ${item.title} project`}
                >
                  <motion.img
                    src={item.src ?? placeholder}
                    alt={item.title ?? "Project screenshot"}
                        loading="lazy"
                        decoding="async"
                        variants={imageVariant}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        whileHover={{ scale: 1.05 }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = placeholder;
                        }}
                      />
                  <div className="thumb-overlay" aria-hidden="true">
                    <div className="overlay-content">
                      <span className="overlay-title">{item.title}</span>
                      <div className="overlay-arrow">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M7 17L17 7M17 7H8M17 7V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </a>

                <div className="portfolio-card-body">
                  <div className="card-content">
                    <h3 className="portfolio-title">{item.title}</h3>
                    <p className="portfolio-desc">{item.description}</p>
                  </div>

                  <div className="portfolio-links">
                    {item.github && (
                      <motion.a
                        href={item.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="portfolio-link"
                        aria-label={`View ${item.title} source code on GitHub`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>Source Code</span>
                        <svg width="16" height="16" viewBox="0 0 20 19" fill="none" aria-hidden="true">
                          <path
                            d="M4.66667 1.66675H18V15.0001M18 1.66675L2 17.6667L18 1.66675Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.article>
            ))
          ) : (
            <motion.div 
              className="portfolio-empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <h3>No projects available</h3>
                <p>Check back soon for updates</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}